import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useReportSharing } from '@/hooks/chat/useReportSharing';
import { images } from '@/assets/images';
import { 
  TrendingUp, DollarSign, Calendar, CheckCircle2, 
  AlertCircle, Clock, Home, Lock 
} from 'lucide-react';

const SharedReport: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { getSharedReport } = useReportSharing();
  const [report, setReport] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (token) {
      loadReport();
    }
  }, [token]);

  const loadReport = async () => {
    try {
      setLoading(true);
      const data = await getSharedReport(token!);
      setReport(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950/20 to-slate-950 flex items-center justify-center">
        <div className="text-white">Loading report...</div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950/20 to-slate-950 flex items-center justify-center p-4">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-500" />
              Report Not Available
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">
              {error || 'This report link is invalid or has expired.'}
            </p>
            <Link to="/">
              <Button className="w-full">Return Home</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const reportData = report.report_data;
  const readinessScore = reportData.readinessScore;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950/20 to-slate-950">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex justify-center mb-6">
          <Link to="/">
            <img
              src={images.equiforgeLogoFlat}
              alt="EquiForge"
              className="h-10 md:h-12 w-auto object-contain drop-shadow-md"
            />
          </Link>
        </div>

        <div className="max-w-4xl mx-auto space-y-6">
          {/* Title */}
          <div className="text-center">
            <h1 className="text-3xl font-bold text-white mb-2">
              Homebuyer Readiness Report
            </h1>
            <p className="text-slate-300">
              Shared by {reportData.userName || 'Anonymous'}
            </p>
            <Badge variant="outline" className="mt-2">
              <Lock className="h-3 w-3 mr-1" />
              Secure View-Only Access
            </Badge>
          </div>

          {/* Overall Score */}
          <Card className="bg-gradient-to-br from-purple-600 to-blue-600 border-none text-white">
            <CardContent className="p-6">
              <div className="text-center">
                <TrendingUp className="h-12 w-12 mx-auto mb-3" />
                <h2 className="text-2xl font-bold mb-2">Overall Readiness Score</h2>
                <div className="text-6xl font-bold mb-4">
                  {Math.round(readinessScore.overall * 100)}%
                </div>
                <Progress 
                  value={readinessScore.overall * 100} 
                  className="h-3 bg-white/20"
                />
              </div>
            </CardContent>
          </Card>

          {/* Score Breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-green-500" />
                  Financial Readiness
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold mb-2">
                  {Math.round(readinessScore.financial * 100)}%
                </div>
                <Progress value={readinessScore.financial * 100} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-blue-500" />
                  Timeline Readiness
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold mb-2">
                  {Math.round(readinessScore.timeline * 100)}%
                </div>
                <Progress value={readinessScore.timeline * 100} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-purple-500" />
                  Knowledge Level
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold mb-2">
                  {Math.round(readinessScore.knowledge * 100)}%
                </div>
                <Progress value={readinessScore.knowledge * 100} />
              </CardContent>
            </Card>
          </div>

          {/* Recommendations */}
          {reportData.recommendations && reportData.recommendations.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Recommendations</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {reportData.recommendations.map((rec: string, idx: number) => (
                    <li key={idx} className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm">{rec}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Next Steps */}
          {reportData.nextSteps && reportData.nextSteps.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Next Steps</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {reportData.nextSteps.map((step: string, idx: number) => (
                    <li key={idx} className="flex items-start gap-2">
                      <div className="h-6 w-6 rounded-full bg-primary/20 text-primary flex items-center justify-center text-xs font-bold flex-shrink-0">
                        {idx + 1}
                      </div>
                      <span className="text-sm">{step}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* CTA */}
          <Card className="bg-primary/10 border-primary/20">
            <CardContent className="p-6 text-center">
              <Home className="h-12 w-12 mx-auto mb-3 text-primary" />
              <h3 className="text-xl font-bold mb-2">Ready to Start Your Journey?</h3>
              <p className="text-muted-foreground mb-4">
                Talk to C.L.A.R.A. to get your own personalized readiness assessment
              </p>
              <Link to="/chatbot">
                <Button size="lg">
                  Start Your Assessment
                </Button>
              </Link>
            </CardContent>
          </Card>

          {/* Footer */}
          <div className="text-center text-xs text-muted-foreground">
            <p className="flex items-center justify-center gap-2">
              <Lock className="h-3 w-3" />
              This report is view-only and does not expose sensitive financial information
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SharedReport;
